import * as am5 from '@amcharts/amcharts5';
import { Grid, Paper, Typography } from '@mui/material';
import i18next from 'i18next';
import { useCallback, useContext, useEffect, useState } from 'react';
import { hidePieSliceLabel } from 'services/common/ChartUtils';
import useStyles from 'style/js-style/containers/dashboard/TabStyle';

import useSelectedAccountId from 'components/common/hooks/useSelectedAccountId';
import { useRef } from 'react';
import { isAbortError } from 'services/common/ApiUtils';
import { ERROR } from 'services/common/Constants';
import {
  EcovadisPerformanceColor,
  EcovadisPerformanceOrder,
  EcovadisScoresEnum,
} from 'services/common/EcovadisUtils';
import { formatNumber } from 'services/common/Utils';
import { getEcovadisScoresCount } from 'services/edge/EcovadisService';
import { Context } from 'states/Store';
import RiskScorePieChart from '../RiskScorePieChart';
import RiskSubScoresPieChart from '../RiskSubScoresPieChart';

function TabDashboardEcovadis() {
  const { classes } = useStyles();
  const [state, dispatch] = useContext(Context);
  const [mappedScoreCount, setMappedScoreCount] = useState({});
  const accountId = useSelectedAccountId();

  const abortCtrlRef = useRef();

  const fetchScoreCount = useCallback(
    (query) => {
      if (abortCtrlRef.current) {
        abortCtrlRef.current.abort();
      }

      abortCtrlRef.current = new AbortController();
      const signal = abortCtrlRef.current.signal;

      if (accountId && state.account) {
        (async () => {
          try {
            const res = await getEcovadisScoresCount(
              accountId,
              query,
              null,
              signal
            );

            let mappedScoresCountsObj = {};
            for (const scoreCounts of res.content) {
              // Selected score from result
              const selectedScore = scoreCounts.score;
              // Selected score counts from result
              const selectedCounts = scoreCounts.counts;

              // Total tps for this score
              const total = selectedCounts
                ?.map((d) => d.count)
                .reduce((partialSum, a) => partialSum + a, 0);

              mappedScoresCountsObj[selectedScore] = {
                data: selectedCounts
                  .map((range) => ({
                    id: range.rangeLabel,
                    itemLabel: i18next.t(`ecovadis.performance.${range.rangeLabel?.toLowerCase()}`),
                    type: `ecovadis_${selectedScore}`,
                    value: range.count,
                    formattedValue: formatNumber(range.count),
                    settings: {
                      fill: am5.color(
                        EcovadisPerformanceColor[
                          range.rangeLabel?.toLowerCase()
                        ]
                      ),
                    },
                    labelSettings: {
                      forceHidden: hidePieSliceLabel(range.count, total),
                    },
                  }))
                  .sort((a, b) =>
                    EcovadisPerformanceOrder[a.id] >
                    EcovadisPerformanceOrder[b.id]
                      ? 1
                      : -1
                  ),
                total: formatNumber(total),
              };
            }
            setMappedScoreCount(mappedScoresCountsObj);
          } catch (e) {
            if (!isAbortError(e)) {
              console.error('error', e);
              dispatch({
                type: 'ALERT',
                alert: { type: ERROR, msg: e.message },
              });
            }
          }
        })();
      }
    },
    [accountId, dispatch, state.account]
  );

  // Build filters query from graph filters
  useEffect(() => {
    let queryObj = {};

    if (state.graphFilters?.pie?.length > 0) {
      queryObj = state.graphFilters.pie.reduce((result, item) => {
        const { id, type } = item;
        if (!result[type]) {
          result[type] = [];
        }
        result[type].push(id);
        return result;
      }, {});
    }
    fetchScoreCount(queryObj);

    return () => {
      abortCtrlRef.current?.abort();
    };
  }, [fetchScoreCount, state.graphFilters?.pie]);

  const getEcovadisSubScores = () => {
    return Object.keys(mappedScoreCount)
      ?.filter((score) => score !== EcovadisScoresEnum.GLOBAL)
      .map((score) => ({
        scoreLabel: i18next.t(`ecovadis.scores.${score}`),
        data: mappedScoreCount?.[score],
      }));
  };

  return (
    <Paper elevation={0} className={classes.paperContainer}>
      <Grid container spacing={2} style={{ height: '100%' }}>
        <Grid item xs={4} lg={4} key="ecovadis-global">
          <Paper className={classes.graphPaper}>
            <Typography variant="h3" className={classes.graphAreaTitle}>
              <div style={{ display: 'flex' }}>
                <span>{i18next.t(`ecovadis.dashboard.title`)}</span>
              </div>
            </Typography>
            <RiskScorePieChart
              subTab={'ecovadis'}
              scoreCount={mappedScoreCount?.[EcovadisScoresEnum.GLOBAL]}
            />
          </Paper>
        </Grid>
        <Grid item xs={8} lg={8} key="ecovadis-subscores">
          <RiskSubScoresPieChart
            title={i18next.t(`ecovadis.dashboard.subScoresTitle`)}
            riskLabel="ecovadis"
            subScores={getEcovadisSubScores()}
          />
        </Grid>
      </Grid>
    </Paper>
  );
}

export default TabDashboardEcovadis;
